import { COLORS } from '../plTheme';
import { startOfWeek, addWeeks, format, endOfWeek, addDays } from 'date-fns';

export const roundNumber = (num: string) => {
  const parts = num.split(',');
  return parts.length > 1
    ? (
        Math.round(
          (parseInt(parts.join(''), 10) / Math.pow(1000, parts.length - 1)) *
            10,
        ) / 10
      )?.toString() + ['K', 'M', 'B'][parts.length - 2]
    : parts[0];
};

export const ProductColorMapping: { name: string; color: string }[] = [
  {
    name: 'asp',
    color: '#0055DC',
  },
  {
    name: 'edge',
    color: '#5B8FF9',
  },
  {
    name: 'cloudsecure',
    color: 'RGBA(0, 88, 227, 0.3)',
  },
  {
    name: 'others',
    color: 'RGBA(126, 133, 142, 0.13)',
  },
];

export const formatHireDate = (date: string | undefined, isOrg = false) => {
  if (date) {
    let formatedDate = date?.includes('-') ? date : +date;
    const dateObject = new Date(formatedDate);
    const month = String(dateObject.getMonth() + 1).padStart(2, '0');
    const year = dateObject.getFullYear() % 100;
    return isOrg ? `${month}/${year}` : `(DoH: ${month}/${year})`;
  } else return '';
};

export const generateWeeksOfYear = (year: number) => {
  const weeks = [];
  let current = startOfWeek(new Date(year, 0, 1), { weekStartsOn: 1 }); // Monday start
  let count = 1;

  while (current.getFullYear() <= year) {
    const weekStart = current; // This is already Monday
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

    // Calculate Friday of the week (4 days after Monday)
    const friday = addDays(weekStart, 4);

    if (weekStart.getFullYear() > year) break;

    // Only use dates that are in the target year for the label
    let labelStart = weekStart;
    let labelEnd = friday;

    // If Monday is from previous year, use Jan 1st of target year
    if (weekStart.getFullYear() < year) {
      labelStart = new Date(year, 0, 1);
    }

    // If Friday is from next year, use Dec 31st of target year
    if (friday.getFullYear() > year) {
      labelEnd = new Date(year, 11, 31);
    }

    // Skip weeks that don't have any days in the target year
    if (labelStart.getFullYear() === year || labelEnd.getFullYear() === year) {
      weeks.push({
        label: `Week ${count} (${format(labelStart, 'MMM d')} - ${format(
          labelEnd,
          'MMM d',
        )})`,
        start: weekStart,
        end: friday,
      });
    }

    current = addWeeks(current, 1);
    count++;
  }

  return weeks;
};

export const generateWeeksOfYearWithQuarters = (year: number) => {
  const weeks = [];
  let current = startOfWeek(new Date(year, 0, 1), { weekStartsOn: 1 }); // Monday start
  let count = 1;

  // Helper function to get quarter from date
  const getQuarter = (date: Date) => {
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    if (month <= 3) return 1;
    if (month <= 6) return 2;
    if (month <= 9) return 3;
    return 4;
  };

  // Helper function to determine which quarter to show based on week number within quarter
  const getQuarterForWeek = (
    weekStart: Date,
    friday: Date,
    weekNumberInYear: number,
  ) => {
    // Get the quarter that the week primarily falls into
    const startQuarter = getQuarter(weekStart);
    const endQuarter = getQuarter(friday);

    // Use the quarter where most of the week falls
    const primaryQuarter = getQuarter(addDays(weekStart, 2)); // Use Wednesday as reference

    // Calculate week number within the quarter
    const quarterStartMonth = (primaryQuarter - 1) * 3; // 0, 3, 6, 9
    const quarterStartDate = startOfWeek(new Date(year, quarterStartMonth, 1), {
      weekStartsOn: 1,
    });

    // Find which week this is within the quarter
    let weekInQuarter = 1;
    let tempDate = quarterStartDate;

    while (tempDate < weekStart) {
      tempDate = addWeeks(tempDate, 1);
      weekInQuarter++;
    }

    // If week number in quarter is less than 7, use previous quarter
    if (weekInQuarter < 7) {
      if (primaryQuarter === 1) {
        return `Q4 ${(year - 1).toString().slice(-2)}`;
      } else {
        return `Q${primaryQuarter - 1} ${year.toString().slice(-2)}`;
      }
    } else {
      // return `Q${primaryQuarter} ${year.toString().slice(-2)}`;
      return `Q${primaryQuarter} TD`;
    }
  };

  while (current.getFullYear() <= year) {
    const weekStart = current; // This is already Monday
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

    // Calculate Friday of the week (4 days after Monday)
    const friday = addDays(weekStart, 4);

    if (weekStart.getFullYear() > year) break;

    // Only use dates that are in the target year for the label
    let labelStart = weekStart;
    let labelEnd = friday;

    // If Monday is from previous year, use Jan 1st of target year
    if (weekStart.getFullYear() < year) {
      labelStart = new Date(year, 0, 1);
    }

    // If Friday is from next year, use Dec 31st of target year
    if (friday.getFullYear() > year) {
      labelEnd = new Date(year, 11, 31);
    }

    // Skip weeks that don't have any days in the target year
    if (labelStart.getFullYear() === year || labelEnd.getFullYear() === year) {
      // Get the quarter based on the logic
      const quarter = getQuarterForWeek(weekStart, friday, count);

      weeks.push({
        label: `Week ${count} (${format(labelStart, 'MMM d')} - ${format(
          labelEnd,
          'MMM d',
        )})`,
        start: weekStart,
        end: friday,
        quarter: quarter,
        week: `${year}_W${count}`,
      });
    }

    current = addWeeks(current, 1);
    count++;
  }

  return weeks;
};

// Function to format quarter and week display
// Updated to accept year parameter and use same logic as WeeklyCoaching component
export const formatQuarterWeek = (quarter: number, week: number, year: number = 2025): string => {
  // Calculate Q1 start date dynamically - same logic as WeeklyCoaching/index.tsx
  // Business quarters typically start on the Monday closest to January 1st
  const jan1 = new Date(year, 0, 1);
  const dayOfWeek = jan1.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  let q1StartDate;
  
  if (dayOfWeek === 1) {
    // January 1st is Monday - use it
    q1StartDate = new Date(year, 0, 1);
  } else if (dayOfWeek >= 2 && dayOfWeek <= 4) {
    // January 1st is Tue-Thu - go to previous Monday
    const daysBack = dayOfWeek - 1;
    q1StartDate = new Date(year, 0, 1 - daysBack);
  } else {
    // January 1st is Fri(5), Sat(6), or Sun(0) - go to next Monday
    const daysForward = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    q1StartDate = new Date(year, 0, 1 + daysForward);
  }
  
  const quarterStartDates = [
    new Date(q1StartDate), // Q1
    new Date(q1StartDate.getTime() + (13 * 7 * 24 * 60 * 60 * 1000)), // Q2 starts 13 weeks after Q1
    new Date(q1StartDate.getTime() + (26 * 7 * 24 * 60 * 60 * 1000)), // Q3 starts 26 weeks after Q1
    new Date(q1StartDate.getTime() + (39 * 7 * 24 * 60 * 60 * 1000)), // Q4 starts 39 weeks after Q1
  ];

  const quarterStart = quarterStartDates[quarter - 1];
  const weekStart = new Date(quarterStart);
  weekStart.setDate(quarterStart.getDate() + (week - 1) * 7);

  const monthName = weekStart.toLocaleDateString('en-US', { month: 'short' });
  const day = weekStart.getDate();
  const yearString = weekStart.getFullYear();

  return `Q${quarter} Week ${week} (${monthName} ${day}, ${yearString})`;
};
